import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Drawer,
  Button,
  Stack,
  Snackbar,
  Alert,
  Divider,
  CircularProgress,
  Chip,
  TextField,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useWarRoomSocket } from "../hooks/useWarRoomSocket";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
// import { getCityCounts } from "../services/warRoomService";
import carGreen from "../assets/vehicle-icons/car-green.png";
import carYellow from "../assets/vehicle-icons/car-yellow.png";
import carRed from "../assets/vehicle-icons/car-red.png";
import carBlue from "../assets/vehicle-icons/car-blue.png";
import VehicleAlerts from "../components/war-room/VehicleAlerts";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PlaceIcon from "@mui/icons-material/Place";
import GlobalAlerts from "../components/war-room/GlobalAlerts";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  getCityMapCenter,
  getNearestMappedCity,
  getOperationalCityForCoordinates,
  indiaDefaultCenter,
  normalizeIndiaCityName,
} from "../utils/indiaCityMap";
// import WarRoomCharts from "./WarRoomCharts";
// import { useNavigate } from "react-router-dom";
//import { useNavigate } from "react-router-dom";

/* ================= MAP CONFIG ================= */

const containerStyle = {
  width: "100%",
  height: "75vh",
};

const indiaCenter = { lat: indiaDefaultCenter.lat, lng: indiaDefaultCenter.lng };
const statusColors = {
  active: "#22c55e",
  charging: "#3b82f6",
  idle: "#f59e0b",
  offline: "#ef4444",
};

const providerColors = {
  opspod: "#0ea5e9",
  altmobility: "#22c55e",
  total: "#f59e0b",
};

const lightMapStyles = [
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#cbd5e1" }, { weight: 1 }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f8fbff" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#eefaf1" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#e2e8f0" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#64748b" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c7e9fb" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#2563eb" }] },
];

const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#07111f" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#94a3b8" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#07111f" }, { weight: 3 }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#1e3a5f" }, { weight: 1 }] },
  { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#cbd5e1" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#dbeafe" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#061426" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#08213a" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#0b243d" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#64748b" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#15395a" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#020b16" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#64748b" }] },
];

function createVehicleMarkerIcon(status: string, markerSize: number) {
  if (!window.google?.maps) return undefined;

  return {
    url: getVehicleIconByStatus(status),
    scaledSize: new window.google.maps.Size(markerSize, markerSize),
    anchor: new window.google.maps.Point(markerSize / 2, markerSize / 2),
  };
}

function getPercent(value: number, total: number) {
  return total ? (value / total) * 100 : 0;
}

function formatPercent(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "0";
  return value.toFixed(2).replace(/\.?0+$/, "");
}

function getWarRoomApiBase() {
  const raw = import.meta.env.VITE_API_GATEWAY_URL || "http://localhost:8000";
  return String(raw).replace(/\/api\/?$/, "");
}

function formatAlertTime(value?: string | number) {
  if (!value) return "Just now";
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return "Just now";
  const minutes = Math.max(0, Math.round((Date.now() - time) / 60000));
  if (minutes < 1) return "Now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} hr ago`;
}

function getVehicleCity(v: any) {
  const lat = Number(v?.lat);
  const lng = Number(v?.lng);
  const coordinateCity = getOperationalCityForCoordinates(lat, lng);
  if (coordinateCity) return coordinateCity;

  const city = normalizeIndiaCityName(v?.city);
  if (city && city.toLowerCase() !== "unknown") return city;

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return getNearestMappedCity(lat, lng);
  }
  return undefined;
}

function getVehicleMarkerSize(zoom: number, status: string) {
  const zoomSize =
    zoom >= 14 ? 52 :
    zoom >= 12 ? 48 :
    zoom >= 10 ? 42 :
    zoom >= 8 ? 38 :
    zoom >= 6 ? 34 :
    30;
  const statusBoost = status === "MOVING" ? 2 : status === "UNKNOWN" ? -2 : 0;
  return Math.max(28, Math.min(58, zoomSize + statusBoost));
}

function normalizeTelemetryBoolean(value: any) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value > 0;

  const normalized = String(value ?? "").trim().toLowerCase();
  if (["on", "true", "1", "yes", "running"].includes(normalized)) return true;
  if (["off", "false", "0", "no", "stopped"].includes(normalized)) return false;

  return false;
}

function hasReceivedVehicleLocation(v: any) {
  const lat = Number(v?.lat);
  const lng = Number(v?.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (v?.hasGPS === false) return false;
  if (lat === 0 && lng === 0) return false;

  return true;
}

/* ================= COMPONENT ================= */

export default function WarRoom() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // Alerts Drawer state
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [recentAlerts, setRecentAlerts] = useState<any[]>([]);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY!,
  });

  const vehicles = useWarRoomSocket();
  // const navigate = useNavigate();

  // IMMOBILIZE STATE
  // const [immobilizeStatus, setImmobilizeStatus] = useState<"ON" | "OFF">("OFF");
  const [immobilizeLoading, setImmobilizeLoading] = useState(false);

  const [commandStatus, setCommandStatus] = useState("");

  /* ================= NORMALIZE DATA ================= */

  const normalizedVehicles = useMemo(() => {
  return vehicles
    .filter(hasReceivedVehicleLocation)
    .map((v: any) => {
      const id = v.vehicleId || v.uniqueId;
      const speed = Number(v.speed ?? v.Speed ?? 0) || 0;
      const ignition = normalizeTelemetryBoolean(v.ignition ?? v.IGN);
      return {
        id,
        source:
          String(v.vehicleId || "").startsWith("opspod:")
            ? "opspod"
            : String(v.vehicleId || "").startsWith("alt:")
            ? "altmobility"
            : "unknown",
        vehicleNo:
          v.vehicleNo ||
          v.registrationNumber ||
          v.regNo ||
          v.vin ||
          (v.vehicleId?.includes(":")
            ? v.vehicleId.split(":")[1]
            : v.vehicleId) ||
          "NA",
        imei: String(v.imei || v.Imeino || ""),
        lat: Number(v.lat),
        lng: Number(v.lng),
        city: v.city,
        speed,
        ignition,
        lastUpdated: v.lastUpdated,
        location: v.location,
        todayActivity: v.todayActivity,
      };
    });
}, [vehicles]);

// ✅ CORRECT PLACE
const handleImmobilizeToggle = async () => {
  if (!selectedVehicle) return;

  try {
    setImmobilizeLoading(true);
    setCommandStatus("Sending command...");

const isEngineOn =
  getVehicleStatus(selectedVehicle) === "MOVING" ||
  getVehicleStatus(selectedVehicle) === "IDLE";

const action = isEngineOn ? "On" : "Off";

    console.log("🚀 Sending IMEI:", selectedVehicle.imei);

    // 🔥 ADD THIS BLOCK HERE
    if (selectedVehicle.speed > 0 && action === "On") {
      const confirmAction = window.confirm(
        "⚠️ Vehicle is moving. Immobilizing will stop it immediately. Proceed?"
      );

      if (!confirmAction) {
        setImmobilizeLoading(false);
        setCommandStatus("❌ Cancelled by user");
        return;
      }
    }

    const API_URL = import.meta.env.VITE_API_GATEWAY_URL;
    const res = await fetch(`${API_URL}/api/war-room/immobilize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        imei: selectedVehicle.imei,
        action,
      }),
    });

    const data = await res.json().catch(() => null);

    console.log("🔐 API RESPONSE:", data);

    if (!res.ok || !data?.success) {
      throw new Error(data?.error || data?.message || "API failed");
    }

    // 🔥 show queued/success
    if (data.success) {
      // setImmobilizeStatus(prev => (prev === "ON" ? "OFF" : "ON"));
      setCommandStatus("✅ Command Sent");
    } else {
      setCommandStatus("❌ Failed");
    }

  } catch (err: any) {
    console.error("❌ Immobilize failed", err);
    setCommandStatus(`Failed: ${err.message || "Command failed"}`);
  } finally {
    setImmobilizeLoading(false);
  }
};

  // --- City counts with unknowns merged ---
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null); // 'altmobility' | 'opspod' | null
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const cityCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    normalizedVehicles.forEach((v: any) => {
      const id = String(v.id || "");
      if (selectedCompany === 'opspod' && !(id.startsWith('opspod:') || (v.source && String(v.source).toLowerCase() === 'opspod') || id.toLowerCase().includes('opspod') || (v.source && String(v.source).toLowerCase().includes('opspod')))) return;
      if (selectedCompany === 'altmobility' && !(id.startsWith('alt:') || (v.source && v.source.toLowerCase() === 'altmobility'))) return;
      const matchedCity = getVehicleCity(v);
      if (matchedCity && matchedCity.toLowerCase() !== "unknown") {
        counts[matchedCity] = (counts[matchedCity] || 0) + 1;
      }
    });
    return counts;
  }, [normalizedVehicles, selectedCompany]);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [mapCenter, setMapCenter] = useState(indiaCenter);
  const [mapZoom, setMapZoom] = useState(5);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);





  const vehicleStats = useMemo(() => {
  let total = 0;
  let opspod = 0;
  let altmobility = 0;

  normalizedVehicles.forEach((v: any) => {
    total++;

    const id = String(v.id || "");

    if (id.startsWith("opspod:")) opspod++;
    else if (id.startsWith("alt:")) altmobility++;
  });

  return { total, opspod, altmobility };
}, [normalizedVehicles]);

const liveStatusStats = useMemo(() => {
  const total = vehicleStats.total;
  const opspodPct = getPercent(vehicleStats.opspod, total);
  const altmobilityPct = getPercent(vehicleStats.altmobility, total);
  const other = Math.max(0, total - vehicleStats.opspod - vehicleStats.altmobility);
  const otherPct = getPercent(other, total);
  return {
    total,
    opspod: vehicleStats.opspod,
    altmobility: vehicleStats.altmobility,
    other,
    opspodPct,
    altmobilityPct,
    otherPct,
  };
}, [vehicleStats]);

const cityPerformance = useMemo(() => {
  const total = Object.values(cityCounts).reduce((sum, count) => sum + count, 0);
  return Object.entries(cityCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([city, count]) => ({
      city,
      count,
      pct: getPercent(count, total),
    }));
}, [cityCounts]);

useEffect(() => {
  let mounted = true;
  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${getWarRoomApiBase()}/api/war-room/alerts`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (mounted) setRecentAlerts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch recent alerts", err);
    }
  };

  fetchAlerts();
  const interval = window.setInterval(fetchAlerts, 10000);
  return () => {
    mounted = false;
    window.clearInterval(interval);
  };
}, []);





const handleVehicleSelect = useCallback(async (vehicle: any) => {
  // OpsPod already has address
  if (vehicle.source !== "altmobility") {
    setSelectedVehicle(vehicle);
    return;
  }

  // AltMobility → derive address from lat/lng
  if (vehicle.lat && vehicle.lng) {
    setSelectedVehicle({
      ...vehicle,
      location: "Fetching address...",
    });

    const address = await reverseGeocodeWithMaps(
      vehicle.lat,
      vehicle.lng
    );

    setSelectedVehicle({
      ...vehicle,
      location: address,
    });
  } else {
    setSelectedVehicle(vehicle);
  }
}, []);

  /* ✅ IMPORTANT: SHOW VEHICLES ONLY AFTER CITY CLICK */

const filteredVehicles = useMemo(() => {
  let list = normalizedVehicles;
  // Company filter
  if (selectedCompany === 'opspod') {
    list = list.filter((v) => {
      const id = String(v.id || "");
      const src = v.source ? String(v.source).toLowerCase() : "";
      return id.startsWith('opspod:') || src === 'opspod' || id.toLowerCase().includes('opspod') || src.includes('opspod');
    });
  } else if (selectedCompany === 'altmobility') {
    list = list.filter((v) => {
      const id = String(v.id || "");
      return id.startsWith('alt:') || (v.source && v.source.toLowerCase() === 'altmobility');
    });
  }
  // City filter
  if (selectedCity) {
    list = list.filter((v) => {
      const matchedCity = getVehicleCity(v);
      return matchedCity === selectedCity;
    });
  }
  // 🔍 Search filter
  if (searchQuery.trim()) {
    list = list.filter((v) =>
      String(v.vehicleNo || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }
  return list;
}, [normalizedVehicles, selectedCity, selectedCompany, searchQuery]);


useEffect(() => {
  if (!searchQuery.trim()) return;

  const matches = normalizedVehicles.filter(
    (v) =>
      v.vehicleNo &&
      v.vehicleNo.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ✅ If only ONE vehicle matches → auto open drawer
  if (matches.length === 1) {
    const vehicle = matches[0];

    setSelectedVehicle(vehicle);
    setMapCenter({ lat: vehicle.lat, lng: vehicle.lng });
    setMapZoom(16);
  } else {
    // ❌ Multiple or zero matches → close drawer
    setSelectedVehicle(null);
  }
}, [searchQuery, normalizedVehicles]);


  /* ================= CITY CLICK ================= */

  const handleCityClick = (city: string) => {
    setSelectedCity(city);
    setSelectedVehicle(null);

    const cityCenter = getCityMapCenter(city);
    if (cityCenter) {
      setMapCenter({
        lat: cityCenter.lat,
        lng: cityCenter.lng,
      });
      setMapZoom(cityCenter.zoom);
    } else {
      const cityVehicles = normalizedVehicles.filter((v) => getVehicleCity(v) === city);
      if (cityVehicles.length) {
        const center = cityVehicles.reduce(
          (acc, v) => ({
            lat: acc.lat + Number(v.lat || 0),
            lng: acc.lng + Number(v.lng || 0),
          }),
          { lat: 0, lng: 0 },
        );
        setMapCenter({
          lat: center.lat / cityVehicles.length,
          lng: center.lng / cityVehicles.length,
        });
        setMapZoom(12);
      } else {
        setMapCenter(indiaCenter);
        setMapZoom(5);
      }
    }
  };

  /* ================= COPY HELPERS ================= */

 const handleCopyCoordinates = (vehicle: any) => {
  if (!vehicle?.lat || !vehicle?.lng) return;

  const mapLink = `https://www.google.com/maps/search/?api=1&query=${vehicle.lat},${vehicle.lng}`;

  navigator.clipboard.writeText(mapLink);
  setCopyMsg("Google Maps link copied");
};


  const handleCopyAddress = (vehicle: any) => {
    if (!vehicle?.location) return;
    navigator.clipboard.writeText(vehicle.location);
    setCopyMsg("Address copied");
  };

  /**
   * 📥 Download all vehicles data (OpsPod + Alt Mobility) as CSV
   */
  const handleDownloadVehicles = async () => {
    setIsDownloading(true);
    try {
      // Use API Gateway URL from environment or fallback to localhost:8000
      const apiGatewayUrl = import.meta.env.VITE_API_GATEWAY_URL || "http://localhost:8000";
      const exportUrl = `${apiGatewayUrl}/api/war-room/export/vehicles`;

      console.log(`📥 Downloading from: ${exportUrl}`);

      const response = await fetch(exportUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers.get("content-disposition");
      let filename = "vehicles_export.csv";
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setCopyMsg(`✅ Downloaded ${filename}`);
    } catch (error) {
      console.error("Download error:", error);
      setCopyMsg(`❌ Download failed: ${error}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const panelSx = {
    borderRadius: 2,
    border: "1px solid",
    borderColor: isDark ? "rgba(30, 64, 175, 0.55)" : "rgba(148, 163, 184, 0.38)",
    background: isDark ? "rgba(3, 12, 24, 0.84)" : "rgba(255, 255, 255, 0.9)",
    color: isDark ? "#e5edf8" : "#0f172a",
    boxShadow: isDark ? "0 18px 44px rgba(0,0,0,0.34)" : "0 14px 34px rgba(15,23,42,0.10)",
    backdropFilter: "blur(16px)",
  };

  const mapOptions = useMemo(() => ({
    styles: isDark ? darkMapStyles : lightMapStyles,
    fullscreenControl: true,
    streetViewControl: false,
    mapTypeControl: true,
    zoomControl: true,
    clickableIcons: false,
    gestureHandling: "greedy",
    restriction: {
      latLngBounds: {
        north: 36.5,
        south: 6,
        west: 66,
        east: 98,
      },
      strictBounds: false,
    },
  }), [isDark]);

  const donutBackground = `conic-gradient(${providerColors.opspod} 0 ${liveStatusStats.opspodPct}%, ${providerColors.altmobility} ${liveStatusStats.opspodPct}% ${liveStatusStats.opspodPct + liveStatusStats.altmobilityPct}%, ${providerColors.total} ${liveStatusStats.opspodPct + liveStatusStats.altmobilityPct}% 100%)`;

  if (!isLoaded) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }


  return (
    <Box sx={{ p: { xs: 1, md: 1.25 }, overflowX: 'hidden', color: isDark ? "#e5edf8" : "#0f172a" }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.45rem', md: '1.85rem' }, lineHeight: 1.05, fontWeight: 950 }}>
          🇮🇳 IoT War Room
        </Typography>
        <Button
          variant="outlined"
          startIcon={<NotificationsIcon />}
          onClick={() => setAlertsOpen(true)}
        >
          Alerts
        </Button>
      </Box>
      {/* Global Alerts Drawer */}
      <Drawer
        anchor="right"
        open={alertsOpen}
        onClose={() => setAlertsOpen(false)}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 400 } } }}
      >
        <GlobalAlerts />
      </Drawer>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1.1, mb: 1.1 }}>
        {[
          { key: null, label: "Total Vehicles", value: vehicleStats.total, caption: "Live", color: providerColors.total },
          { key: "altmobility", label: "Alt Mobility", value: vehicleStats.altmobility, caption: "Live Vehicles", color: providerColors.altmobility },
          { key: "opspod", label: "OpsPod", value: vehicleStats.opspod, caption: "Live Vehicles", color: providerColors.opspod },
        ].map((item) => {
          const active = selectedCompany === item.key;
          return (
            <Paper
              key={item.label}
              onClick={() => {
                setSelectedCompany(item.key);
                setSelectedCity(null);
                setSelectedVehicle(null);
                setMapCenter(indiaCenter);
                setMapZoom(5);
              }}
              sx={{
                ...panelSx,
                p: 1.45,
                textAlign: "center",
                cursor: "pointer",
                borderColor: active ? item.color : isDark ? "rgba(148,163,184,0.28)" : "rgba(148,163,184,0.45)",
                boxShadow: active ? `0 0 0 1px ${item.color}, 0 18px 44px ${item.color}22` : panelSx.boxShadow,
                background: active
                  ? isDark ? `${item.color}18` : `${item.color}10`
                  : panelSx.background,
                transition: "border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 950, color: isDark ? "#cbd5e1" : "#334155" }}>{item.label}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1.05, color: active ? item.color : "inherit" }}>{item.value}</Typography>
              <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 850 }}>{item.caption}</Typography>
            </Paper>
          );
        })}
      </Box>

      <Paper sx={{ ...panelSx, p: 1.25, mb: 1.1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={950}>City Performance</Typography>
          <Button size="small" onClick={() => { setSelectedCompany(null); setSelectedCity(null); setMapCenter(indiaCenter); setMapZoom(5); }} sx={{ fontWeight: 900 }}>View All Cities</Button>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(6, minmax(0, 1fr))" }, gap: 1 }}>
          {cityPerformance.slice(0, 12).map((item) => (
            <Box key={item.city} onClick={() => handleCityClick(item.city)} sx={{ p: 1, borderRadius: 1.5, cursor: "pointer", border: selectedCity === item.city ? `1px solid ${statusColors.active}` : `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(226,232,240,0.9)"}`, background: selectedCity === item.city ? `${statusColors.active}18` : isDark ? "rgba(15,23,42,0.58)" : "rgba(248,250,252,0.72)" }}>
              <Stack direction="row" spacing={0.6} alignItems="center">
                <PlaceIcon sx={{ color: statusColors.active, fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.city}</Typography>
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.1, mt: 0.5 }}>{item.count}</Typography>
              <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 800 }}>Live Vehicles</Typography>
              <Box sx={{ mt: 0.8, height: 5, borderRadius: 10, background: isDark ? "rgba(148,163,184,0.16)" : "rgba(203,213,225,0.7)", overflow: "hidden" }}>
                <Box sx={{ width: `${item.pct}%`, minWidth: item.pct > 0 ? 2 : 0, height: "100%", background: statusColors.active, borderRadius: 10 }} />
              </Box>
              <Typography variant="caption" sx={{ color: statusColors.active, fontWeight: 900 }}>{formatPercent(item.pct)}%</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      <Grid container spacing={1.1} sx={{ mb: 1.1 }}>
        <Grid item xs={12} lg={8}>
          <Paper
            sx={{
              ...panelSx,
              overflow: "hidden",
              borderColor: isDark ? "rgba(56,189,248,0.28)" : "rgba(37,99,235,0.22)",
              background: isDark
                ? "linear-gradient(135deg, rgba(6,30,45,0.94), rgba(10,50,48,0.86))"
                : "linear-gradient(135deg, rgba(239,246,255,0.96), rgba(236,253,245,0.9))",
              boxShadow: isDark ? "0 28px 70px rgba(0,0,0,0.44)" : "0 24px 64px rgba(15,23,42,0.16)",
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between" sx={{ p: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: statusColors.active, boxShadow: `0 0 10px ${statusColors.active}` }} />
                <Typography variant="subtitle2" fontWeight={950}>Live Fleet Map</Typography>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75}>
                <TextField
                  size="small"
                  placeholder="Search vehicle number"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    minWidth: { xs: "100%", sm: 240 },
                    "& .MuiInputBase-root": {
                      height: 34,
                      color: isDark ? "#e5edf8" : "#0f172a",
                      background: isDark ? "rgba(15,23,42,0.72)" : "rgba(248,250,252,0.9)",
                    },
                  }}
                />
                {searchQuery && (
                  <Button size="small" variant="outlined" onClick={() => { setSearchQuery(""); setSelectedVehicle(null); }}>
                    Clear
                  </Button>
                )}
                <Button variant="contained" color="success" size="small" startIcon={isDownloading ? <CircularProgress size={16} /> : <FileDownloadIcon />} onClick={handleDownloadVehicles} disabled={isDownloading} sx={{ whiteSpace: "nowrap", fontWeight: 900 }}>
                  Download
                </Button>
              </Stack>
            </Stack>
            <Box
              sx={{
                mx: 1,
                mb: 1,
                height: { xs: "58vh", md: "66vh" },
                minHeight: { xs: 430, md: 560 },
                borderRadius: 2,
                overflow: "hidden",
                border: isDark ? "1px solid rgba(56,189,248,0.24)" : "1px solid rgba(37,99,235,0.22)",
                boxShadow: isDark ? "0 18px 42px rgba(0,0,0,0.32)" : "0 18px 42px rgba(15,23,42,0.16)",
              }}
            >
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={mapCenter}
                zoom={mapZoom}
                options={mapOptions}
              >
                {filteredVehicles.map(v => (
                  <SmoothVehicleMarker
                    key={v.id}
                    vehicle={v}
                    status={getVehicleStatus(v)}
                    zoom={mapZoom}
                    onClick={() => handleVehicleSelect(v)}
                  />
                ))}
              </GoogleMap>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={1.1} sx={{ height: "100%" }}>
            <Paper sx={{ ...panelSx, p: 1.4 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={950}>Live Summary</Typography>
                <Button size="small" href="/warroom-summary" sx={{ fontWeight: 900 }}>View All</Button>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 130, height: 130, borderRadius: "50%", background: donutBackground, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <Box sx={{ width: 78, height: 78, borderRadius: "50%", background: isDark ? "#071526" : "#ffffff", display: "grid", placeItems: "center", border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(226,232,240,0.9)"}` }}>
                    <Box textAlign="center">
                      <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1 }}>{liveStatusStats.total}</Typography>
                      <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 800 }}>Total</Typography>
                    </Box>
                  </Box>
                </Box>
                <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                  {[
                    ["OpsPod", liveStatusStats.opspod, liveStatusStats.opspodPct, providerColors.opspod, "opspod"],
                    ["Alt Mobility", liveStatusStats.altmobility, liveStatusStats.altmobilityPct, providerColors.altmobility, "altmobility"],
                    ["Total", liveStatusStats.total, 100, providerColors.total, null],
                  ].map(([label, count, pct, color, provider]) => (
                    <Stack
                      key={String(label)}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      onClick={() => {
                        setSelectedCompany(provider as string | null);
                        setSelectedCity(null);
                        setSelectedVehicle(null);
                        setMapCenter(indiaCenter);
                        setMapZoom(5);
                      }}
                      sx={{
                        px: 0.65,
                        py: 0.35,
                        mx: -0.65,
                        borderRadius: 1,
                        cursor: "pointer",
                        background: selectedCompany === provider ? `${String(color)}18` : "transparent",
                        "&:hover": { background: `${String(color)}12` },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: String(color) }} />
                        <Typography variant="caption" sx={{ fontWeight: 800 }}>{label}</Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: isDark ? "#cbd5e1" : "#334155", fontWeight: 900 }}>{count} ({formatPercent(Number(pct))}%)</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
              <Divider sx={{ my: 1.2, borderColor: isDark ? "rgba(148,163,184,0.18)" : "rgba(226,232,240,0.9)" }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 800 }}>Auto Refresh</Typography>
                <Chip size="small" color="success" label="ON" sx={{ height: 22, fontWeight: 900 }} />
              </Stack>
            </Paper>

            <Paper sx={{ ...panelSx, p: 1.4, flex: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={950}>Recent Alerts</Typography>
                <Button size="small" onClick={() => setAlertsOpen(true)} sx={{ fontWeight: 900 }}>View All</Button>
              </Stack>
              <Stack spacing={0.9}>
                {recentAlerts.slice(0, 4).map((alert, index) => {
                  const severity = String(alert.severity || "LOW").toUpperCase();
                  const color = severity === "HIGH" ? "#ef4444" : severity === "MEDIUM" ? "#f59e0b" : "#22c55e";
                  return (
                    <Box key={alert.id || `${alert.vehicleId}-${index}`} sx={{ display: "grid", gridTemplateColumns: "32px 1fr auto", gap: 1, alignItems: "center" }}>
                      <Box sx={{ width: 30, height: 30, borderRadius: 1.5, display: "grid", placeItems: "center", background: `${color}1f`, color }}>
                        <NotificationsIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ display: "block", fontWeight: 900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {String(alert.type || "Vehicle alert").replace(/_/g, " ")}
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", color: isDark ? "#94a3b8" : "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {String(alert.vehicleId || alert.message || "Unknown vehicle").replace("opspod:", "")}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end" spacing={0.25}>
                        <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: 10 }}>{formatAlertTime(alert.eventTime)}</Typography>
                        <Chip size="small" label={severity} sx={{ height: 18, fontSize: 9, fontWeight: 900, color: "#fff", background: color }} />
                      </Stack>
                    </Box>
                  );
                })}
                {!recentAlerts.length && (
                  <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>No recent alerts</Typography>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {false && (
      <Box sx={{ display: "none" }}>

      {/* SEARCH BAR */}
<Paper
  sx={{
    p: { xs: 1.25, md: 1.5 },
    mb: 2,
    display: "flex",
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
    gap: { xs: 1, md: 2 },
    borderRadius: 2,
  }}
>
  <Typography variant="subtitle2" sx={{ minWidth: { xs: 0, sm: 120 } }}>
    🔍 Search Vehicle
  </Typography>

  <input
    type="text"
    placeholder="Enter vehicle number (e.g. KA03AK1683)"
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    style={{
      flex: 1,
      minWidth: 0,
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid #ccc",
      fontSize: 14,
      outline: "none",
    }}
  />

  {searchQuery && (
    <Button
  size="small"
  variant="outlined"
  onClick={() => {
    setSearchQuery("");
    setSelectedVehicle(null);
  }}
>
  Clear
</Button>
  )}

  {/* 📥 DOWNLOAD BUTTON */}
  <Button
    variant="contained"
    color="success"
    startIcon={isDownloading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
    onClick={handleDownloadVehicles}
    disabled={isDownloading}
    sx={{ ml: { xs: 0, sm: 1 }, whiteSpace: 'nowrap' }}
  >
    {isDownloading ? "Downloading..." : "Download All"}
  </Button>
</Paper>


      {/* KPI ROW: Total, Alt Mobility, OpsPod */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: { xs: 1, md: 2 }, mb: 1.5, mt: 1 }}>
        <Paper
          sx={{ p: 1, minWidth: 0, textAlign: 'center', borderRadius: 2, borderLeft: '4px solid #241869ff', cursor: 'pointer', border: selectedCompany === null ? '2px solid #00ff66' : '1px solid #e0e0e0' }}
          onClick={() => { setSelectedCompany(null); setSelectedCity(null); }}
        >
          <Typography variant="subtitle2" fontSize={14}>Total Vehicles</Typography>
          <Typography variant="h5" fontWeight={700}>{vehicleStats.total}</Typography>
          <Typography variant="caption">Live</Typography>
        </Paper>
        <Paper
          sx={{ p: 1, minWidth: 0, textAlign: 'center', borderRadius: 2, borderLeft: '4px solid #00FF66', cursor: 'pointer', border: selectedCompany === 'altmobility' ? '2px solid #00ff66' : '1px solid #e0e0e0' }}
          onClick={() => { setSelectedCompany('altmobility'); setSelectedCity(null); }}
        >
          <Typography variant="subtitle2" fontSize={14}>Alt Mobility</Typography>
          <Typography variant="h5" fontWeight={700}>{vehicleStats.altmobility}</Typography>
          <Typography variant="caption">Live Vehicles</Typography>
        </Paper>
        <Paper
          sx={{ p: 1, minWidth: 0, textAlign: 'center', borderRadius: 2, borderLeft: '4px solid #0084FF', cursor: 'pointer', border: selectedCompany === 'opspod' ? '2px solid #00ff66' : '1px solid #e0e0e0' }}
          onClick={() => { setSelectedCompany('opspod'); setSelectedCity(null); }}
        >
          <Typography variant="subtitle2" fontSize={14}>OpsPod</Typography>
          <Typography variant="h5" fontWeight={700}>{vehicleStats.opspod}</Typography>
          <Typography variant="caption">Live Vehicles</Typography>
        </Paper>
      </Box>

      {/* CITY BOXES ROW */}
      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        {Object.entries(cityCounts)
          .sort((a, b) => b[1] - a[1]) // Sort by count descending
          .map(([city, count]) => (
            <Paper
              key={city}
              sx={{
                p: 1,
                minWidth: 110,
                maxWidth: 140,
                flex: '0 1 120px',
                textAlign: 'center',
                borderRadius: 2,
                cursor: 'pointer',
                border: selectedCity === city ? '2px solid #00ff66' : '1px solid #e0e0e0',
                boxShadow: selectedCity === city ? 4 : 1,
                transition: 'border 0.2s, box-shadow 0.2s',
              }}
              onClick={() => handleCityClick(city)}
            >
              <Typography variant="subtitle2" fontSize={13}>{city}</Typography>
              <Typography variant="h6" fontWeight={700}>{count}</Typography>
              <Typography variant="caption">Live Vehicles</Typography>
            </Paper>
          ))}
      </Box>
      {/* MAP */}
      <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={mapZoom}>
        {/* {Object.entries(cityMapCenters).map(([city, pos]) => (
          <Marker
            key={city}
            position={{ lat: pos.lat, lng: pos.lng }}
            label={city}
            onClick={() => handleCityClick(city)}
          />
        ))} */}
        {filteredVehicles.map(v => (
          <SmoothVehicleMarker
  key={v.id}
  vehicle={v}
  status={getVehicleStatus(v)}
  zoom={mapZoom}
onClick={() => handleVehicleSelect(v)}
/>
        ))}
      </GoogleMap>

{/* <WarRoomCharts
  vehicles={vehicles}
  cityCounts={cityCounts}
  stats={vehicleStats}
/> */}
      </Box>
      )}

      {/* DRAWER */}
      <Drawer
        anchor="right"
        open={Boolean(selectedVehicle)}
        onClose={() => setSelectedVehicle(null)}
        PaperProps={{
          sx: {
            width: 420,
            top: "64px",
            height: "calc(100% - 64px)",
            overflowY: "auto",
          },
        }}
      >
        {selectedVehicle && (
          <Box p={3} display="flex" flexDirection="column" gap={1.2}>
<Box
  display="flex"
  alignItems="center"
  justifyContent="space-between"
>
  <Typography variant="h6">🛵 Vehicle Details</Typography>

  <Button
    size="small"
    startIcon={<ArrowBackIcon />}
    onClick={() => setSelectedVehicle(null)}
  >
    Back
  </Button>
</Box>

            <Typography>
  <b>Vehicle No:</b>{" "}
  {selectedVehicle.vehicleNo}

  {/* COMPANY BADGE */}
  <Box
    component="span"
    sx={{
      ml: 1,
      px: 1,
      py: 0.25,
      fontSize: 12,
      borderRadius: 1,
      backgroundColor:
        selectedVehicle.source === "opspod"
          ? "#E3F2FD"
          : "#E8F5E9",
      color:
        selectedVehicle.source === "opspod"
          ? "#1565C0"
          : "#2E7D32",
    }}
  >
    {getProviderLabel(selectedVehicle)}
  </Box>
</Typography>

            <Typography><b>City:</b> {selectedVehicle.city}</Typography>
            <Typography><b>Speed:</b> {selectedVehicle.speed} km/h</Typography>
            <Typography><b>Status:</b> {getVehicleStatus(selectedVehicle)}</Typography>
            <Typography>
              <b>Last Update:</b>{" "}
              {new Date(selectedVehicle.lastUpdated).toLocaleString()}
            </Typography>

            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
              {selectedVehicle.location}
            </Typography>

             {/* COPY */}
            <Stack direction="row" spacing={1} mt={2}>
              <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => handleCopyCoordinates(selectedVehicle)}>
                Copy GPS
              </Button>
              <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => handleCopyAddress(selectedVehicle)}>
                Copy Address
              </Button>
            </Stack>

            {/* TODAY ACTIVITY */}
            {selectedVehicle.todayActivity && (
              <Box mt={4}>
                <Typography variant="subtitle1">📊 Today Activity</Typography>
                <Grid container spacing={1} mt={1}>
                  {(() => {
                   const distance =
  selectedVehicle.todayActivity?.officialDistanceKm ??
  selectedVehicle.todayActivity?.distanceKm ??
  selectedVehicle.todayActivity?.todayDistanceKm ??
  0;
                   const co2 = (distance * 0.12).toFixed(2);
                   const trees = (Number(co2) / 21).toFixed(2);
                    return (
                      <>
                        <ActivityBox
                          label="📏Distance"
                          value={`${distance.toFixed(2)} km`}
                        />
                        <ActivityBox
                          label="🌿CO₂ Saved"
                          value={`${co2} kg`}
                        />
                        <ActivityBox
                          label="🌱Trees"
                          value={trees}
                        />
                      </>
                    );
                  })()}
                  {/* <ActivityBox label="Running" value={formatDuration(selectedVehicle.todayActivity.runningSeconds)} />
                  <ActivityBox label="Idle" value={formatDuration(selectedVehicle.todayActivity.idleSeconds)} />
                  <ActivityBox label="Stopped" value={formatDuration(selectedVehicle.todayActivity.stoppedSeconds)} /> */}
                </Grid>
              </Box>
            )}
            {/* 🔒 IMMOBILIZE */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                color: "white",
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                🔒 Immobilize
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Status: {getVehicleStatus(selectedVehicle)}
              </Typography>
              <Typography variant="caption">
                {commandStatus}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                disabled={
  immobilizeLoading || selectedVehicle.source !== "opspod"
}
                onClick={handleImmobilizeToggle}
                sx={{
                  background: "white",
                  color: "#333",
                  fontWeight: 600,
                }}
              >
                {immobilizeLoading
  ? "Processing..."
  : getVehicleStatus(selectedVehicle) === "MOVING" ||
getVehicleStatus(selectedVehicle) === "IDLE"
  ? "Turn OFF"
  : "Turn ON"}
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

<Typography variant="subtitle1" fontWeight={600}>
  🚨 Alerts
</Typography>

{selectedVehicle && (
  <VehicleAlerts vehicleId={selectedVehicle.id} />
)}

            {/* 🚕 Trip Info */}
<Box mt={2}>
  <Typography variant="subtitle1" gutterBottom>
    🛵 Trip Info (Today)
  </Typography>

  <Grid container spacing={1}>
    <Grid item xs={6}>
      <Paper sx={{ p: 1.5 }}>
        <Typography variant="caption">Trip Count</Typography>
        <Typography variant="h6">
          {selectedVehicle.todayActivity?.tripCount || 0}
        </Typography>
      </Paper>
    </Grid>

    <Grid item xs={6}>
      <Paper sx={{ p: 1.5 }}>
        <Typography variant="caption">Current Trip</Typography>
        <Typography variant="h6">
          {(selectedVehicle.todayActivity?.currentTripKm || 0).toFixed(2)} km
        </Typography>
      </Paper>
    </Grid>
  </Grid>
</Box>
          </Box>
        )}
      </Drawer>

      <Snackbar open={Boolean(copyMsg)} autoHideDuration={2000} onClose={() => setCopyMsg(null)}>
        <Alert severity="success" variant="filled">{copyMsg}</Alert>
      </Snackbar>
    </Box>
  );
}
function getVehicleIconByStatus(status: string) {
  switch (status) {
    case "MOVING":
      return carGreen;

    case "IDLE":
      return carYellow;

    case "IGN_OFF":
    case "OFFLINE":
      return carRed;

    default:
      return carBlue;
  }
}

/* ================= HELPERS ================= */

function getVehicleStatus(vehicle: any) {
  const speed = Number(vehicle?.speed ?? 0) || 0;
  const ignition = normalizeTelemetryBoolean(vehicle?.ignition);

  if (speed > 0) return "MOVING";
  if (!ignition) return "IGN_OFF";
  return "IDLE";
}

function ActivityBox({ label, value }: any) {
  return (
    <Grid item xs={6}>
      <Paper sx={{ p: 1, textAlign: "center" }}>
        <Typography variant="caption">{label}</Typography>
        <Typography variant="h6">{value}</Typography>
      </Paper>
    </Grid>
  );
}

function getProviderLabel(vehicle: any) {
  const id = String(vehicle.vehicleId || vehicle.id || "");
  const src = vehicle.source ? String(vehicle.source).toLowerCase() : "";

  if (src === "altmobility" || id.startsWith("alt:")) {
    return "Alt Mobility";
  }

  if (id.startsWith("opspod:") || src === "opspod" || src.includes("opspod")) {
    return "OpsPod";
  }

  return "Unknown";
}


function reverseGeocodeWithMaps(lat: number, lng: number): Promise<string> {
  return new Promise((resolve) => {
    if (!window.google || !window.google.maps) {
      resolve("Address unavailable");
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      { location: { lat, lng } },
      (results, status) => {
        if (status === "OK" && results && results.length > 0) {
          resolve(results[0].formatted_address);
        } else {
          resolve("Address unavailable");
        }
      }
    );
  });
}


/* ================= SMOOTH MARKER ================= */

const SmoothVehicleMarker = memo(function SmoothVehicleMarker({ vehicle, status, zoom, onClick }: any) {
  const position = useMemo(
    () => ({ lat: Number(vehicle.lat), lng: Number(vehicle.lng) }),
    [vehicle.lat, vehicle.lng],
  );
  const markerSize = getVehicleMarkerSize(Number(zoom) || 5, status);
  const icon = useMemo(
    () => createVehicleMarkerIcon(status, markerSize),
    [markerSize, status],
  );

  return (
    <Marker
      position={position}
      icon={icon}
      onClick={onClick}
      title={vehicle.vehicleNo || vehicle.id}
    />
  );
}, (prev, next) => (
  prev.vehicle.id === next.vehicle.id &&
  prev.vehicle.lat === next.vehicle.lat &&
  prev.vehicle.lng === next.vehicle.lng &&
  prev.status === next.status &&
  prev.zoom === next.zoom
));
